export interface ResourcePool {
  gold: number;
  wood: number;
  stone: number;
  mana: number;
}

export interface BuildingState {
  type: number;
  level: number;
  lastProductionClaimAt: string;
}

export interface BuildOrder {
  target: number;
  targetLevel: number;
  startedAt: string;
  finishesAt: string;
}

export interface TrainingOrder {
  unitId: string;
  count: number;
  startedAt: string;
  finishesAt: string;
  // Tier 1..10. Older saves (pre T1-T10) omit the field; treat as 1.
  tier?: number;
}

export interface PromotionOrder {
  unitId: string;
  count: number;
  fromTier: number;
  toTier: number;
  startedAt: string;
  finishesAt: string;
}

export interface HealingOrder {
  unitId: string;
  // C# record field is `Count`; serializer emits camelCase `count`. The
  // earlier `totalCount` TS name was a typo that silently rendered as NaN
  // in active healing/revival rows.
  count: number;
  startedAt: string;
  finishesAt: string;
  // Tier of the units being healed/revived. Defaults to 1.
  tier?: number;
}

export interface CastleStateSnapshot {
  buildings: BuildingState[];
  resources: ResourcePool;
  activeOrders: BuildOrder[];
  maxBuildSlots: number;
  garrison: Record<string, number>;
  trainingOrders: TrainingOrder[];
  // Tier-promotion orders in flight. Older saves omit it → treat as []. Units
  // are already debited from garrison[unit@fromTier]; on finish they land in
  // garrison[unit@toTier].
  promotionOrders?: PromotionOrder[] | null;
  hospital: HealingOrder[];
  temple: HealingOrder[];
  warHallPaidSlotUnlocked: boolean;
  shieldUntil: string | null;
  beginnerShieldUntil: string | null;
  soulShards: number;
  heroXp: number;
  // Crystal-of-Heroes rework (2026-05-22). Server returns nulls when the
  // CastleState was persisted before the rework — treat as empty maps.
  heroShards?: Record<string, number> | null;
  universalShards?: Record<string, number> | null;   // key = rarity int as string
  heroCrystals?: Record<string, number> | null;      // key = tier int as string
  // Inventory rework (2026-05-23). Keys = item ids from InventoryCatalog
  // ("spd-1h", "bundle-gold-m" и т.д.).
  inventory?: Record<string, number> | null;
  // Manual-heal rework (2026-05-22). HospitalPool = wounded waiting for
  // the player to start a paid heal batch; TemplePool = dead defender units
  // waiting for paid revival. Both are dicts unitId → count.
  hospitalPool?: Record<string, number> | null;
  templePool?: Record<string, number> | null;
}

export interface ResearchBonuses {
  production: number;
  marchSpeed: number;
  buildSpeed: number;
  trainSpeed: number;
  attack: number;
}

export interface CastleResponse {
  state: CastleStateSnapshot;
  armyPower: number;
  isShielded: boolean;
  isBeginnerShielded: boolean;
  maxMarchSlots: number;
  maxAllowedWarHallLevel: number;
  castleLevel: number;
  marchCapacity: number;
  safeStorage: ResourcePool;
  powerBreakdown: PowerBreakdown;
  researchBonuses: ResearchBonuses;
  serverNow: string;
}

export interface PowerBreakdown {
  army: number;
  buildings: number;
  research: number;
  heroes: number;
  total: number;
}

export interface BuildingCatalogLevel {
  level: number;
  cost: ResourcePool;
  durationSeconds: number;
  productionPerHour: number;
  // Human-readable effect summary for this level, e.g. "Производство: 260/ч".
  // Computed server-side via BuildingEffectDescriber so frontend doesn't need
  // to know the per-building formulas.
  effect: string;
}

export interface BuildingCatalogEntry {
  type: number;
  typeName: string;
  name: string;
  maxLevel: number;
  // Minimum Castle level at which this building becomes visible / buildable.
  // 1 = always (default). Used by CastleScreen to gate the UI.
  revealAtCastleLevel: number;
  producedResource: number | null;
  producedResourceName: string | null;
  baseProductionPerHour: number;
  levels: BuildingCatalogLevel[];
}

export const BuildingType = {
  Castle: 0,
  GoldMine: 1,
  Lumberjack: 2,
  Quarry: 3,
  ManaAltar: 4,
  Warehouse: 5,
  Barracks: 6,
  ArcheryRange: 7,
  MageTower: 8,
  SignatureForge: 9,
  Hospital: 10,
  WarHall: 11,
  Academy: 12,
  HeroHall: 13,
  BuilderHut: 14,
  TempleOfResurrection: 15,
} as const;

export const BuildingIcon: Record<number, string> = {
  0: "🏰",
  1: "⛏️",
  2: "🪓",
  3: "🪨",
  4: "🔮",
  5: "🏚️",
  6: "⚔️",
  7: "🏹",
  8: "🧙",
  9: "🔥",
  10: "🏥",
  11: "🚩",
  12: "📚",
  13: "👑",
  14: "🔨",
  15: "⛩️",
};
